import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Star, Calendar, Users, TrendingUp } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { getProject, getProjects } from "@/lib/api";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProject(slug).then((project) => {
      setProject(project);
      return getProjects().then((all) => {
        setRelated(all.filter((p) => p.slug !== slug).slice(0, 3));
      });
    }).catch(() => setProject(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="project-detail-loading" className="pt-40 mx-auto max-w-5xl px-6 md:px-12">
        <div className="h-8 w-40 rounded-full bg-white/5 animate-pulse" />
        <div className="mt-6 h-16 w-3/4 rounded-2xl bg-white/5 animate-pulse" />
        <div className="mt-10 aspect-[16/9] rounded-3xl bg-white/5 animate-pulse" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pt-40 mx-auto max-w-3xl px-6 md:px-12 text-center">
        <p className="font-mono text-xs text-zinc-500 uppercase">404</p>
        <h1 className="font-display text-4xl mt-4">Project not found</h1>
        <Link to="/projects" className="mt-6 inline-flex items-center gap-2 text-nexus-cyan">
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="project-detail-page" className="pt-32 pb-20">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-12">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/projects" className="hover:text-foreground">Projects</Link> · {project.title}
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-5xl">
            {project.title}
          </h1>
          <div className="mt-6 flex flex-wrap gap-2">
            {project.industry.map((ind) => (
              <span key={ind} className="rounded-full glass border-white/10 px-3 py-1 text-[11px] uppercase tracking-wider text-zinc-300">
                {ind}
              </span>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 relative aspect-[16/9] rounded-3xl overflow-hidden border border-white/[0.08]">
            <img src={project.image} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-nexus-ink/50 via-transparent to-transparent" />
          </div>
        </Reveal>

        {/* Metrics overlay */}
        <Stagger className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricCard icon={Calendar} label="Timeline" value={project.timeline} />
          <MetricCard icon={Users} label="Team size" value={project.team_size} />
          <MetricCard icon={TrendingUp} label="Client" value={project.client} />
        </Stagger>
      </section>

      {/* Challenge & Solution */}
      <section className="mx-auto max-w-7xl px-6 md:px-12 mt-24 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-7 space-y-12">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ The challenge ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">What we set out to solve.</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">{project.challenge}</p>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Our approach ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">How we delivered.</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">{project.solution}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-5 space-y-6">
          <div className="rounded-2xl glass border-white/[0.08] p-7">
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Project info</p>
            <dl className="mt-4 space-y-3 text-sm">
              <Row k="Client" v={project.client} />
              <Row k="Industry" v={project.industry.join(", ")} />
              <Row k="Services" v={project.services.join(", ")} />
              <Row k="Timeline" v={project.timeline} />
              <Row k="Team" v={project.team_size} />
            </dl>
          </div>
          <div className="rounded-2xl glass border-white/[0.08] p-7">
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Tech stack</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {project.technologies.map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-widest text-zinc-300 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Results / Metrics */}
      {project.metrics && project.metrics.length > 0 && (
        <section data-testid="project-results" className="mx-auto max-w-7xl px-6 md:px-12 mt-24">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Results ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">Numbers that matter.</h2>
          </Reveal>
          <Stagger className="mt-10 grid md:grid-cols-3 gap-5">
            {project.metrics.map((m) => (
              <Item key={m.label}>
                <div className="rounded-3xl glass border-white/[0.08] p-10">
                  <div className="font-display text-5xl md:text-6xl tracking-tighter text-gradient-cyan">{m.value}</div>
                  <div className="mt-4 font-medium">{m.label}</div>
                  <div className="text-sm text-zinc-400 mt-1">{m.detail}</div>
                </div>
              </Item>
            ))}
          </Stagger>
        </section>
      )}

      {/* Testimonial */}
      {project.testimonial && (
        <section className="mx-auto max-w-4xl px-6 md:px-12 mt-24">
          <Reveal>
            <div className="rounded-3xl glass border-white/[0.08] p-10 md:p-14">
              <div className="flex items-center gap-1 mb-6">
                {[0,1,2,3,4].map((i) => <Star key={i} className="h-4 w-4 fill-nexus-cyan text-nexus-cyan" />)}
              </div>
              <p className="font-display text-2xl md:text-3xl leading-[1.25] tracking-tight">
                "{project.testimonial.quote}"
              </p>
              <div className="mt-6">
                <div className="font-medium">{project.testimonial.name}</div>
                <div className="text-sm text-zinc-500">{project.testimonial.role}</div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* Gallery */}
      {project.gallery && project.gallery.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 md:px-12 mt-24">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Gallery ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">Take a closer look.</h2>
          </Reveal>
          <Stagger className="mt-10 grid md:grid-cols-2 gap-5">
            {project.gallery.map((img, i) => (
              <Item key={i}>
                <div className="aspect-[16/10] rounded-3xl overflow-hidden border border-white/[0.08]">
                  <img src={img} alt={`${project.title} gallery ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              </Item>
            ))}
          </Stagger>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 md:px-12 mt-24">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Related ]</p>
            <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight">Similar projects.</h2>
          </Reveal>
          <Stagger className="mt-10 grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Item key={p.id}>
                <Link to={`/projects/${p.slug}`} className="group block rounded-3xl overflow-hidden border border-white/[0.08] aspect-[4/5] relative">
                  <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-nexus-ink via-nexus-ink/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <h3 className="font-display text-2xl tracking-tight">{p.title}</h3>
                    <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{p.summary}</p>
                  </div>
                </Link>
              </Item>
            ))}
          </Stagger>
        </section>
      )}

      {/* Final CTA */}
      <section className="mx-auto max-w-5xl px-6 md:px-12 mt-24">
        <div className="rounded-[2rem] glass border-white/[0.08] p-12 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">
            Want results like these?
          </h2>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-7 py-4 text-sm hover:brightness-110 transition">
            Schedule a consultation <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value }) {
  return (
    <Item>
      <div className="rounded-2xl glass border-white/[0.08] p-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10">
            <Icon className="h-4 w-4 text-nexus-cyan" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="font-cabinet font-medium text-base mt-0.5">{value}</div>
          </div>
        </div>
      </div>
    </Item>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-zinc-500 font-mono text-[11px] uppercase tracking-wider shrink-0 pt-0.5">{k}</dt>
      <dd className="text-right">{v}</dd>
    </div>
  );
}
